import { useState, useEffect, useCallback, useMemo } from "react";
import { useProjectActivity } from "../../../hooks/useProjectActivity";

import { useProjectResorceMappingApi } from "../../../hooks/useProjectResource";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { useAuth } from "../../../contexts/AuthContext";
import { useLoading } from "../../../contexts/LoadingContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import { useRbac } from "../../../hooks/RbacContext";

const getTasksWithTemp = (projectId, tasks) => {
  const storageKey = `tempRows_${projectId}`;
  const savedTempTasks = JSON.parse(localStorage.getItem(storageKey) || "[]");
  const validTempTasks = savedTempTasks
    .filter(
      (task) =>
        task.task_id &&
        task.task_id.startsWith("temp-") &&
        !tasks.some(
          (apiTask) => apiTask.task_id.toString() === task.task_id.toString()
        )
    )
    .map((task) => {
      const now = new Date().toISOString();
      const updatedResourceMappings = (task.resource_mappings || []).map(
        (resource) => {
          const resourceBudgetHours =
            resource.resource_budget_hours !== undefined
              ? Number(resource.resource_budget_hours)
              : 0;

          const estimatedHours = Number(task.estimated_hours);

          // Calculate allocation percentage based on resource budget hours and estimated hours
          let allocationPc = 0;
          if (estimatedHours > 0) {
            allocationPc = (resourceBudgetHours / estimatedHours) * 100;
            allocationPc = Math.round(allocationPc * 100) / 100; // Round to 2 decimal places
          }

          const obj = {
            task_resource_mapping_id:
              resource.task_resource_mapping_id || `temp-${uuidv4()}`,
            task_id: task.task_id,
            resource_id: Number(resource.resource_id),
            allocation_pc: allocationPc,
            resource_budget_hours: resourceBudgetHours,
            // Add allocation_hours for UI compatibility
            allocation_hours: resourceBudgetHours,
            resource_actual_hours:
              resource.resource_actual_hours !== undefined
                ? Number(resource.resource_actual_hours)
                : null,
            is_active: true,
            publish_type: "PENDING",
            rejected_by: null,
            rejected_on: "",
            last_published_on: "",
            approved_by: null,
            approved_on: "",
            created_on: resource.created_on || now,
            updated_on: now,
            __typename: "TaskResourceMapping",
          };
          return obj;
        }
      );

      // Convert Unix timestamps to date strings for edit form
      const convertDate = (dateValue) => {
        if (!dateValue) return "";
        try {
          // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
          let dateObj;
          if (
            typeof dateValue === "number" ||
            /^\d{13,}$/.test(String(dateValue))
          ) {
            const timestamp = parseInt(dateValue);
            // Validate timestamp range (reasonable date range)
            if (timestamp > 0 && timestamp < 9999999999999) {
              dateObj = new Date(timestamp);
            } else {
              console.warn("Invalid timestamp value:", dateValue);
              return "";
            }
          } else {
            dateObj = new Date(dateValue);
          }

          // Check if the date is valid
          if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date value:", dateValue);
            return "";
          }

          return dateObj.toISOString().split("T")[0]; // Return YYYY-MM-DD format
        } catch (e) {
          console.warn("Error converting date:", dateValue, e);
          return "";
        }
      };

      const tempTaskObj = {
        ...task,
        task_id: task.task_id, // keep temp id as string
        project_id: Number(task.project_id),
        milestone_code: task.milestone_code,
        parent_task_id: task.parent_task_id
          ? Number(task.parent_task_id)
          : null,
        estimated_hours: Number(task.estimated_hours),
        start_date: convertDate(task.start_date),
        end_date: convertDate(task.end_date),
        is_active: Boolean(task.is_active),
        created_by: Number(task.created_by),
        publish_type: task.publish_type,
        rejected_by: task.rejected_by,
        rejected_on: task.rejected_on,
        last_published_on: task.last_published_on,
        approved_by: task.approved_by,
        approved_on: task.approved_on,
        resource_mappings: updatedResourceMappings,
        __typename: "Task",
      };
      // Keep allocation_hours for UI compatibility
      console.log("[DEBUG] Valid temp task:", tempTaskObj, "types:", {
        task_id: typeof tempTaskObj.task_id,
        project_id: typeof tempTaskObj.project_id,
        milestone_code: typeof tempTaskObj.milestone_code,
        estimated_hours: typeof tempTaskObj.estimated_hours,
        created_by: typeof tempTaskObj.created_by,
      });
      return tempTaskObj;
    });

  if (validTempTasks.length === 0) {
    localStorage.removeItem(storageKey);
  }

  const normalizedApiTasks = (tasks || []).map((task) => {
    // Convert Unix timestamps to date strings for edit form
    const convertDate = (dateValue) => {
      if (!dateValue) return "";
      try {
        // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
        let dateObj;
        if (
          typeof dateValue === "number" ||
          /^\d{13,}$/.test(String(dateValue))
        ) {
          const timestamp = parseInt(dateValue);
          // Validate timestamp range (reasonable date range)
          if (timestamp > 0 && timestamp < 9999999999999) {
            dateObj = new Date(timestamp);
          } else {
            console.warn("Invalid timestamp value:", dateValue);
            return "";
          }
        } else {
          dateObj = new Date(dateValue);
        }

        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          console.warn("Invalid date value:", dateValue);
          return "";
        }

        return dateObj.toISOString().split("T")[0]; // Return YYYY-MM-DD format
      } catch (e) {
        console.warn("Error converting date:", dateValue, e);
        return "";
      }
    };

    const obj = {
      ...task,
      task_id: task.task_id, // keep as string for consistency
      project_id: Number(task.project_id),
      milestone_code: task.milestone_code,
      parent_task_id: task.parent_task_id ? Number(task.parent_task_id) : null,
      estimated_hours: Number(task.estimated_hours),
      start_date: convertDate(task.start_date),
      end_date: convertDate(task.end_date),
      is_active: Boolean(task.is_active),
      created_by: Number(task.created_by),
      publish_type: task.publish_type,
      rejected_by: task.rejected_by,
      rejected_on: task.rejected_on,
      last_published_on: task.last_published_on,
      approved_by: task.approved_by,
      approved_on: task.approved_on,
      resource_mappings: (task.resource_mappings || []).map((resource) => {
        const resourceBudgetHours = Number(resource.resource_budget_hours);
        const estimatedHours = Number(task.estimated_hours);

        // Calculate allocation percentage based on resource budget hours and estimated hours
        let allocationPc = 0;
        if (estimatedHours > 0) {
          allocationPc = (resourceBudgetHours / estimatedHours) * 100;
          allocationPc = Math.round(allocationPc * 100) / 100; // Round to 2 decimal places
        }

        const resObj = {
          ...resource,
          resource_id: Number(resource.resource_id),
          allocation_pc: allocationPc,
          resource_budget_hours: resourceBudgetHours,
          // Add allocation_hours for UI compatibility
          allocation_hours: resourceBudgetHours,
        };
        return resObj;
      }),
      __typename: "Task",
    };
    delete obj.allocation_hours;
    return obj;
  });

  const allTasks = [...validTempTasks, ...normalizedApiTasks];
  console.log("[DEBUG] getTasksWithTemp - final allTasks:", allTasks);
  return allTasks;
};

export const useProjectActivityViewModel = (projectId) => {
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [approvalConfirmation, setApprovalConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [isNewRow, setIsNewRow] = useState(false);
  const [unavailableResourceIds, setUnavailableResourceIds] = useState([]);
  const [projectDetails, setProjectDetails] = useState(null);
  const { permissions } = useRbac();
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const [resourceConfirmation, setResourceConfirmation] = useState(null);

  const {
    getTasksByProject,
    createTask: apiCreateTask,
    updateTask: apiUpdateTask,
    deleteTask: apiDeleteTask,
    checkResourceAvailability,
    cacheOperations: { readProjectTasks, writeProjectTasks },
    updateTaskStatus,
  } = useProjectActivity();
  const {
    getProjects,
    getProjectById,
    cacheOperations: { readProjects, writeProjects },
  } = useProjectApi();

  const {
    getProjectResourcesByProjectId,
    cacheOperations: {
      readProjectResourcesByProjectId,
      writeProjectResourcesByProjectId,
    },
  } = useProjectResorceMappingApi();

  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map((id) =>
        parseInt(id.trim())
      )
    : [];

  const isSuperAdmin = superAdminRoleIds.includes(
    parseInt(permissions?.globalRole?.role_id)
  );

  const fetchData = useCallback(async () => {
    if (loading || !projectId) return;
    setLoading(true);
    setError(null);

    try {
      const [
        tasksResult,
        projectResourcesResult,
        projectResult,
        projectDetails,
      ] = await Promise.all([
        getTasksByProject(parseInt(projectId)),
        getProjectResourcesByProjectId(parseInt(projectId)),
        getProjectById(parseInt(projectId)),
        getProjects(isSuperAdmin),
      ]);

      console.log("Project Result:", projectResult);

      if (
        !tasksResult.success ||
        !projectResourcesResult.success ||
        !projectResult.success ||
        !projectDetails.success
      ) {
        throw new Error("Failed to fetch required data");
      }

      const allTasks = getTasksWithTemp(projectId, tasksResult.data || []);
      writeProjectResourcesByProjectId(
        projectId,
        projectResourcesResult.data?.GetProjectResourceMappingsByProjectId || []
      );
      writeProjectTasks(parseInt(projectId), allTasks);
      writeProjects(projectDetails.data);
      // Store project details for date validation
      if (projectResult.data) {
        console.log("Setting project details:", projectResult.data);
        // Set the single project details
        setProjectDetails(projectResult.data);
      } else {
        console.warn("No project details found in API response");
        setProjectDetails(null);
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, loading, getProjects, writeProjects]);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const handleRowSave = useCallback(
    async (updatedData) => {
      try {
        showLoading();

        const estimatedHours = updatedData.estimated_hours
          ? parseFloat(updatedData.estimated_hours)
          : 0;

        const resources =
          updatedData.resource_mappings?.map((item) => ({
            resource_id: parseInt(item.resource_id),
            resource_budget_hours: parseFloat(item.allocation_hours),
            allocation_pc: parseFloat(item.allocation_pc) || 0,
          })) || [];

        // Convert Unix timestamps to proper date strings for backend
        const convertDateForBackend = (dateValue) => {
          if (!dateValue) return "";
          try {
            // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
            let dateObj;
            if (
              typeof dateValue === "number" ||
              /^\d{13,}$/.test(String(dateValue))
            ) {
              const timestamp = parseInt(dateValue);
              // Validate timestamp range (reasonable date range)
              if (timestamp > 0 && timestamp < 9999999999999) {
                dateObj = new Date(timestamp);
              } else {
                console.warn("Invalid timestamp value:", dateValue);
                return "";
              }
            } else {
              dateObj = new Date(dateValue);
            }

            // Check if the date is valid
            if (isNaN(dateObj.getTime())) {
              console.warn("Invalid date value:", dateValue);
              return "";
            }

            return dateObj.toISOString().split("T")[0]; // Return YYYY-MM-DD format
          } catch (e) {
            console.warn("Error converting date:", dateValue, e);
            return "";
          }
        };

        const availabilityResult = await checkResourceAvailability(
          convertDateForBackend(updatedData.start_date),
          convertDateForBackend(updatedData.end_date),
          resources,
          updatedData.task_id
        );

        if (!availabilityResult.success) {
          setSuccessModal({
            type: "error",
            title: "Action Failed",
            message: availabilityResult.error,
          });
          return { success: false, error: availabilityResult.error };
        }

        const unavailableResources = availabilityResult.data.resources.filter(
          (r) => !r.is_available
        );

        if (unavailableResources.length > 0) {
          const messages = unavailableResources.map((r) => {
            const resourceName = getResourceName(r.resource_id);
            // Get the actual available hours from the message
            const availableHours =
              r.available_slots?.[0]?.available_hours ||
              r.message.match(/Only\s+([\d.]+)\s+hours\s+available/i)?.[1] ||
              "0.00";
            const taskDate = new Date(
              r.task?.start_date || updatedData.start_date
            )
              .toISOString()
              .split("T")[0];

            console.log("Resource message:", r.message); // Debug log
            console.log("Available hours:", availableHours); // Debug log

            return `<div style="
              margin: 10px 0;
              font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.4;
            ">
              <div style="
                font-size: 13px;
                font-weight: 600;
                color: #333;
                letter-spacing: 0.2px;
              ">${resourceName || "Unknown Resource"}</div>
              <div style="
                margin: 2px 0 0 12px;
                font-size: 12px;
                color: #555;
              ">
                • <span style="font-weight: 500;">${taskDate}</span> - <span style="font-weight: 600;">${parseFloat(
              availableHours
            ).toFixed(2)}</span> hours available
              </div>
            </div>`;
          });

          return new Promise((resolve) => {
            setResourceConfirmation({
              open: true,
              message: `<div style="
                font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                line-height: 1.4;
              ">
               
                ${messages.join("")}
                <div style="
                  margin-top: 16px;
                  font-size: 12px;
                  font-weight: 500;
                  color: #555;
                  letter-spacing: 0.2px;
                ">
                  Would you like to proceed with the current allocation?
                </div>
              </div>`,
              onConfirm: async () => {
                setResourceConfirmation(null);
                const result = await processSave(updatedData);
                resolve(result);
              },
              onCancel: () => {
                setResourceConfirmation(null);
                setUnavailableResourceIds(
                  unavailableResources.map((r) => r.resource_id)
                );
                resolve({ success: false, error: "Operation cancelled" });
              },
              buttonText: "Proceed",
              colorCode: "#FF9800", // Warning color
            });
          });
        }

        // If no unavailable resources, proceed with save
        return await processSave(updatedData);
      } catch (error) {
        console.error("Error in handleRowSave:", error);
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: error.message || "Failed to save milestone",
        });
        return { success: false, error: error.message };
      } finally {
        hideLoading();
      }
    },
    [
      apiUpdateTask,
      checkResourceAvailability,
      projectId,
      readProjectTasks,
      writeProjectTasks,
      showLoading,
      hideLoading,
    ]
  );

  // Helper function to process the actual save
  const processSave = async (updatedData) => {
    // Convert Unix timestamps to proper date strings for backend
    const convertDateForBackend = (dateValue) => {
      if (!dateValue) return "";
      try {
        // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
        let dateObj;
        if (
          typeof dateValue === "number" ||
          /^\d{13,}$/.test(String(dateValue))
        ) {
          const timestamp = parseInt(dateValue);
          // Validate timestamp range (reasonable date range)
          if (timestamp > 0 && timestamp < 9999999999999) {
            dateObj = new Date(timestamp);
          } else {
            console.warn("Invalid timestamp value:", dateValue);
            return "";
          }
        } else {
          dateObj = new Date(dateValue);
        }

        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          console.warn("Invalid date value:", dateValue);
          return "";
        }

        return dateObj.toISOString().split("T")[0]; // Return YYYY-MM-DD format
      } catch (e) {
        console.warn("Error converting date:", dateValue, e);
        return "";
      }
    };

    const processedFormData = {
      project_id: parseInt(projectId),
      milestone_code: updatedData.milestone_code,
      task_name: updatedData.task_name.trim(),
      start_date: convertDateForBackend(updatedData.start_date),
      end_date: convertDateForBackend(updatedData.end_date),
      estimated_hours: parseFloat(updatedData.estimated_hours),
      task_status_code: updatedData.task_status_code || "TO_DO",
      resources:
        updatedData.resource_mappings?.map((item) => ({
          resource_id: parseInt(item.resource_id),
          resource_budget_hours: parseFloat(item.allocation_hours),
          allocation_pc: parseFloat(item.allocation_pc) || 0,
        })) || [],
    };

    const result = await apiUpdateTask(updatedData.task_id, processedFormData);

    if (!result.success) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: result.error,
      });
      return { success: false, error: result.error };
    }

    // Update the local cache with the updated task data
    const currentTasks = readProjectTasks(parseInt(projectId)) || [];
    const taskIndex = currentTasks.findIndex(
      (task) => task.task_id === updatedData.task_id
    );

    if (taskIndex !== -1) {
      const updatedTasks = [...currentTasks];
      updatedTasks[taskIndex] = result.data;
      writeProjectTasks(parseInt(projectId), updatedTasks);
    }

    setSuccessModal({
      type: "success",
      title: "Success",
      message: "Milestone updated successfully",
    });

    setSelectedTask(null);
    setEditingRow(null);

    return { success: true };
  };

  const getResourceName = useCallback(
    (resourceId) => {
      if (!resourceId) return "";
      const resources = readProjectResourcesByProjectId(projectId) || [];
      console.log("Resources from cache:", resources); // Debug log
      const resource = resources.find(
        (pr) => pr.resource_id?.toString() === resourceId?.toString()
      );
      return resource?.resource
        ? `${resource.resource?.first_name} ${resource.resource?.last_name}`
        : "";
    },
    [projectId]
  );

  const getMilestoneName = useCallback((milestoneCode) => {
    if (!milestoneCode) return "";
    return milestoneCode;
  }, []);

  const addNewRow = useCallback(async () => {
    try {
      const now = new Date().toISOString();
      const tempId = `temp-${uuidv4()}`;

      const newRow = {
        task_id: tempId,
        task_name: "",
        milestone_code: "",
        parent_task_id: null,
        start_date: "",
        end_date: "",
        task_status_code: "TO_DO",
        estimated_hours: 0,
        resource_mappings: [],
        project_id: parseInt(projectId),
        is_active: true,
        created_by: parseInt(user.userDetails.resource_id),
        publish_type: "PENDING",
        rejected_by: null,
        rejected_on: "",
        last_published_on: "",
        approved_by: null,
        approved_on: "",
        created_on: now,
        updated_on: now,
        __typename: "Task",
        isNew: true, // Add flag to identify new rows
      };

      const currentTasks = readProjectTasks(parseInt(projectId)) || [];
      const updatedTasks = [newRow, ...currentTasks];
      writeProjectTasks(parseInt(projectId), updatedTasks);

      setSelectedTask(newRow);
      setEditingRow(tempId);
      setIsNewRow(true);
    } catch (error) {
      console.error("Error creating new task:", error);
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to create new milestone",
      });
    }
  }, [projectId, writeProjectTasks, setSelectedTask, user]);

  const handleCancelNew = useCallback(
    (updatedTasks) => {
      try {
        const currentTasks = readProjectTasks(parseInt(projectId)) || [];
        const taskToRemove = currentTasks.find(
          (task) =>
            !updatedTasks.some(
              (updatedTask) => updatedTask.task_id === task.task_id
            )
        );

        if (taskToRemove?.task_id?.startsWith("temp-")) {
          const storageKey = `tempRows_${projectId}`;
          const savedTempTasks = JSON.parse(
            localStorage.getItem(storageKey) || "[]"
          );
          const filteredTempTasks = savedTempTasks.filter(
            (task) => task.task_id !== taskToRemove.task_id
          );
          localStorage.setItem(storageKey, JSON.stringify(filteredTempTasks));
        }

        writeProjectTasks(parseInt(projectId), updatedTasks);
        setSelectedTask(null);
        setEditingRow(null);
        setIsNewRow(false);
        setUnavailableResourceIds([]);
      } catch (error) {
        console.error("Error cancelling task:", error);
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: "Failed to cancel milestone",
        });
      }
    },
    [projectId, writeProjectTasks, readProjectTasks]
  );

  const handleNewRowSave = useCallback(
    async (formData) => {
      try {
        showLoading();

        const estimatedHours = formData.estimated_hours
          ? parseFloat(formData.estimated_hours)
          : 0;

        const resources =
          formData.resource_mappings.map((r) => ({
            resource_id: parseInt(r.resource_id),
            resource_budget_hours: parseFloat(r.allocation_hours),
            allocation_pc: parseFloat(r.allocation_pc) || 0,
          })) || [];

        // Convert Unix timestamps to proper date strings for backend
        const convertDateForBackend = (dateValue) => {
          if (!dateValue) return "";
          try {
            // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
            let dateObj;
            if (
              typeof dateValue === "number" ||
              /^\d{13,}$/.test(String(dateValue))
            ) {
              const timestamp = parseInt(dateValue);
              // Validate timestamp range (reasonable date range)
              if (timestamp > 0 && timestamp < 9999999999999) {
                dateObj = new Date(timestamp);
              } else {
                console.warn("Invalid timestamp value:", dateValue);
                return "";
              }
            } else {
              dateObj = new Date(dateValue);
            }

            // Check if the date is valid
            if (isNaN(dateObj.getTime())) {
              console.warn("Invalid date value:", dateValue);
              return "";
            }

            return dateObj.toISOString().split("T")[0]; // Return YYYY-MM-DD format
          } catch (e) {
            console.warn("Error converting date:", dateValue, e);
            return "";
          }
        };

        const availabilityResult = await checkResourceAvailability(
          convertDateForBackend(formData.start_date),
          convertDateForBackend(formData.end_date),
          resources
        );

        if (!availabilityResult.success) {
          throw new Error(availabilityResult.error);
        }

        const unavailableResources = availabilityResult.data.resources.filter(
          (r) => !r.is_available
        );

        if (unavailableResources.length > 0) {
          const messages = unavailableResources.map((r) => {
            const resourceName = getResourceName(r.resource_id);
            // Get the actual available hours from the message
            const availableHours =
              r.available_slots?.[0]?.available_hours ||
              r.message.match(/Only\s+([\d.]+)\s+hours\s+available/i)?.[1] ||
              "0.00";
            const taskDate = new Date(r.task?.start_date || formData.start_date)
              .toISOString()
              .split("T")[0];

            console.log("Resource message:", r.message); // Debug log
            console.log("Available hours:", availableHours); // Debug log

            return `<div style="
              margin: 10px 0;
              font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              line-height: 1.4;
            ">
              <div style="
                font-size: 13px;
                font-weight: 600;
                color: #333;
                letter-spacing: 0.2px;
              ">${resourceName || "Unknown Resource"}</div>
              <div style="
                margin: 2px 0 0 12px;
                font-size: 12px;
                color: #555;
              ">
                • <span style="font-weight: 500;">${taskDate}</span> - <span style="font-weight: 600;">${parseFloat(
              availableHours
            ).toFixed(2)}</span> hours available
              </div>
            </div>`;
          });

          return new Promise((resolve) => {
            setResourceConfirmation({
              open: true,
              message: `<div style="
                font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                line-height: 1.4;
              ">
                ${messages.join("")}
                <div style="
                  margin-top: 16px;
                  font-size: 12px;
                  font-weight: 500;
                  color: #555;
                  letter-spacing: 0.2px;
                ">
                  Would you like to proceed with the current allocation?
                </div>
              </div>`,
              onConfirm: async () => {
                setResourceConfirmation(null);
                const result = await processNewSave(formData);
                resolve(result);
              },
              onCancel: () => {
                setResourceConfirmation(null);
                setUnavailableResourceIds(
                  unavailableResources.map((r) => r.resource_id)
                );
                resolve({ success: false, error: "Operation cancelled" });
              },
              buttonText: "Proceed",
              colorCode: "#FF9800", // Warning color
            });
          });
        }

        // If no unavailable resources, proceed with save
        return await processNewSave(formData);
      } catch (error) {
        console.error("Error saving new task:", error);
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: error.message || "Failed to save task",
        });
      } finally {
        hideLoading();
        setSelectedTask(null);
        setEditingRow(null);
        setIsNewRow(false);
      }
    },
    [
      apiCreateTask,
      user,
      projectId,
      readProjectTasks,
      writeProjectTasks,
      showLoading,
      hideLoading,
      checkResourceAvailability,
    ]
  );

  // Helper function to process the new task save
  const processNewSave = async (formData) => {
    // Convert Unix timestamps to proper date strings for backend
    const convertDateForBackend = (dateValue) => {
      if (!dateValue) return "";
      try {
        // Handle Unix timestamp (milliseconds) - check if it's a valid timestamp
        let dateObj;
        if (
          typeof dateValue === "number" ||
          /^\d{13,}$/.test(String(dateValue))
        ) {
          const timestamp = parseInt(dateValue);
          // Validate timestamp range (reasonable date range)
          if (timestamp > 0 && timestamp < 9999999999999) {
            dateObj = new Date(timestamp);
          } else {
            console.warn("Invalid timestamp value:", dateValue);
            return "";
          }
        } else {
          dateObj = new Date(dateValue);
        }

        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          console.warn("Invalid date value:", dateValue);
          return "";
        }

        return dateObj.toISOString().split("T")[0]; // Return YYYY-MM-DD format
      } catch (e) {
        console.warn("Error converting date:", dateValue, e);
        return "";
      }
    };

    const processedFormData = {
      project_id: parseInt(projectId),
      milestone_code: formData.milestone_code,
      task_name: formData.task_name.trim(),
      start_date: convertDateForBackend(formData.start_date),
      end_date: convertDateForBackend(formData.end_date),
      estimated_hours: parseFloat(formData.estimated_hours),
      task_status_code: formData.task_status_code || "TO_DO",
      resources:
        formData.resource_mappings.map((r) => ({
          resource_id: parseInt(r.resource_id),
          resource_budget_hours: parseFloat(r.allocation_hours),
          allocation_pc: parseFloat(r.allocation_pc) || 0,
        })) || [],
      created_by: parseInt(user.userDetails.resource_id),
    };

    const result = await apiCreateTask(processedFormData);

    if (result.success) {
      // Apollo cache will automatically update, just remove temporary tasks
      const currentTasks = readProjectTasks(parseInt(projectId)) || [];
      const updatedTasks = currentTasks.filter(
        (task) => !task.task_id.toString().startsWith("temp-")
      );
      writeProjectTasks(parseInt(projectId), updatedTasks);

      setSuccessModal({
        type: "success",
        title: "Success",
        message: "New milestone created successfully",
      });

      return { success: true, data: result.data };
    } else {
      throw new Error(result.error || "Failed to create milestone");
    }
  };

  const handleStatusUpdate = async (taskId) => {
    try {
      showLoading();
      const result = await updateTaskStatus(taskId, "COMPLETED");
      if (result.success) {
        // Update the local cache with the updated task data
        const currentTasks = readProjectTasks(parseInt(projectId)) || [];
        const taskIndex = currentTasks.findIndex(
          (task) => task.task_id === taskId
        );

        if (taskIndex !== -1) {
          const updatedTasks = [...currentTasks];
          updatedTasks[taskIndex] = {
            ...updatedTasks[taskIndex],
            task_status_code: "COMPLETED",
          };
          writeProjectTasks(parseInt(projectId), updatedTasks);
        }

        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Milestone updated successfully",
        });
        fetchData(); // Refresh the tasks list
      } else {
        setSuccessModal({
          type: "error",
          title: "Error",
          message: result.error || "Failed to approve milestone",
        });
      }
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Error",
        message: error.message || "Failed to approve milestone",
      });
    } finally {
      hideLoading();
    }
  };

  const handleApproveClick = (task) => {
    if (task.task_status_code === "COMPLETED") {
      return;
    }

    setApprovalConfirmation({
      open: true,
      task,
      message: "Are you sure you want to Complete this Milestone?",
      onConfirm: () => {
        handleStatusUpdate(task.task_id);
        setApprovalConfirmation(null);
      },
      onCancel: () => setApprovalConfirmation(null),
    });
  };

  const handleDeleteTask = useCallback(
    (task) => {
      setDeleteConfirmation({
        open: true,
        title: "Delete Milestone",
        message: `Are you sure you want to delete the milestone "${task.task_name}"?`,
        onConfirm: async () => {
          setDeleteConfirmation(null);
          try {
            showLoading();
            const result = await apiDeleteTask(task.task_id);
            if (result.success) {
              // Remove from cache
              const currentTasks = readProjectTasks(parseInt(projectId)) || [];
              const updatedTasks = currentTasks.filter(
                (t) => t.task_id !== task.task_id
              );
              writeProjectTasks(parseInt(projectId), updatedTasks);
              setSuccessModal({
                type: "success",
                title: "Deleted",
                message: "Milestone deleted successfully.",
              });
            } else {
              setSuccessModal({
                type: "error",
                title: "Error",
                message: result.error || "Failed to delete activity.",
              });
            }
          } catch (error) {
            setSuccessModal({
              type: "error",
              title: "Error",
              message: error.message || "Failed to delete activity.",
            });
          } finally {
            hideLoading();
          }
        },
        onCancel: () => setDeleteConfirmation(null),
      });
    },
    [
      apiDeleteTask,
      projectId,
      readProjectTasks,
      writeProjectTasks,
      showLoading,
      hideLoading,
    ]
  );
  console.log(
    "readProjectTasks(parseInt(projectId))",
    readProjectTasks(parseInt(projectId))
  );
  return {
    selectedTask,
    successModal,
    deleteConfirmation,
    approvalConfirmation,
    loading,
    error,
    tasks: readProjectTasks(parseInt(projectId)) || [],
    resources: readProjectResourcesByProjectId(projectId).map((pr) => ({
      resource_id: pr.resource_id,
      username: pr.resource?.username || "",
      first_name: pr.resource?.first_name || "",
      last_name: pr.resource?.last_name || "",
    })),
    setSuccessModal,
    setDeleteConfirmation,
    getResourceName,
    refetch: fetchData,
    handleRowSave,
    setSelectedTask,
    addNewRow,
    handleNewRowSave,
    handleCancelNew,
    editingRow,
    setEditingRow,
    isNewRow,
    unavailableResourceIds,
    handleApproveClick,
    resourceConfirmation,
    handleDeleteTask,
    projectDetails,
    projects: readProjects(isSuperAdmin),
  };
};
